import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getTags as fetchTags,
  searchTags as searchTagsService,
  getTag,
  createTag as createTagService,
  updateTag as updateTagService,
  deleteTag as deleteTagService,
  getPopularTags as getPopularTagsService,
  type CreateTagRequest,
  type TagSearchOptions,
} from "../services/outreach/firestore/tagService";
import { OutreachCategory, Tag } from "../types/outreach";

export type TagCategory = OutreachCategory | "global";

interface UseTagManagementOptions {
  category?: TagCategory;
  includeGlobal?: boolean;
  autoLoad?: boolean;
  limit?: number;
}

interface UseTagManagementReturn {
  tags: Tag[];
  popularTags: Tag[];
  loading: boolean;
  popularLoading: boolean;
  error: Error | null;
  popularError: Error | null;
  mutating: boolean;
  refreshTags: (force?: boolean) => Promise<void>;
  searchTags: (
    searchTerm: string,
    options?: Pick<TagSearchOptions, "category" | "limit" | "includeGlobal">
  ) => Promise<Tag[]>;
  createTag: (tagData: CreateTagRequest) => Promise<Tag>;
  updateTag: (
    id: string,
    updates: Partial<CreateTagRequest>
  ) => Promise<Tag | null>;
  deleteTag: (id: string) => Promise<void>;
  fetchPopularTags: (category?: TagCategory, limit?: number) => Promise<Tag[]>;
  clearCache: () => void;
}

const buildCacheKey = (
  category?: TagCategory,
  includeGlobal?: boolean,
  limit?: number
) => {
  return JSON.stringify({ category: category ?? "all", includeGlobal, limit });
};

const sortTags = (tags: Tag[]): Tag[] => {
  return [...tags].sort((a, b) => a.name.localeCompare(b.name));
};

export const useTagManagement = (
  options: UseTagManagementOptions = {}
): UseTagManagementReturn => {
  const { category, limit, autoLoad = true } = options;
  const includeGlobal = options.includeGlobal ?? true;

  const cacheRef = useRef<Map<string, Tag[]>>(new Map());

  const [tags, setTags] = useState<Tag[]>([]);
  const [popularTags, setPopularTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [popularLoading, setPopularLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [popularError, setPopularError] = useState<Error | null>(null);
  const [mutating, setMutating] = useState<boolean>(false);

  const cacheKey = useMemo(
    () => buildCacheKey(category, includeGlobal, limit),
    [category, includeGlobal, limit]
  );

  const shouldIncludeTag = useCallback(
    (tag: Tag): boolean => {
      if (!category) {
        return true;
      }
      if (category === "global") {
        return tag.category === "global";
      }
      return (
        tag.category === category || (includeGlobal && tag.category === "global")
      );
    },
    [category, includeGlobal]
  );

  const loadTags = useCallback(
    async (force = false) => {
      if (!force && cacheRef.current.has(cacheKey)) {
        setTags(cacheRef.current.get(cacheKey) ?? []);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const fetched = await fetchTags({
          category,
          includeGlobal,
          limit,
        });
        const sorted = sortTags(fetched);
        cacheRef.current.set(cacheKey, sorted);
        setTags(sorted);
      } catch (err) {
        console.error("Error loading tags:", err);
        setError(
          err instanceof Error ? err : new Error("Failed to load tags from Firebase")
        );
      } finally {
        setLoading(false);
      }
    },
    [cacheKey, category, includeGlobal, limit]
  );

  const refreshTags = useCallback(
    async (force = true) => {
      if (force) {
        cacheRef.current.delete(cacheKey);
      }
      await loadTags(force);
    },
    [cacheKey, loadTags]
  );

  useEffect(() => {
    if (autoLoad) {
      loadTags();
    }
  }, [autoLoad, loadTags]);

  const clearCache = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  const createManagedTag = useCallback(
    async (tagData: CreateTagRequest): Promise<Tag> => {
      setMutating(true);
      setError(null);
      try {
        const payload: CreateTagRequest = {
          ...tagData,
          category: tagData.category ?? category ?? "global",
        };

        const newTagId = await createTagService(payload);
        const createdTag = (await getTag(newTagId)) ?? {
          id: newTagId,
          name: payload.name,
          description: payload.description,
          category: payload.category,
          color: payload.color,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          usageCount: 0,
          createdBy: payload.createdBy,
        };

        cacheRef.current.clear();

        setTags((prev) => {
          if (!shouldIncludeTag(createdTag)) {
            return prev;
          }
          const updated = prev.filter((tag) => tag.id !== createdTag.id);
          updated.push(createdTag);
          return sortTags(updated);
        });

        return createdTag;
      } catch (err) {
        console.error("Error creating tag:", err);
        const errorToThrow =
          err instanceof Error ? err : new Error("Failed to create tag");
        setError(errorToThrow);
        throw errorToThrow;
      } finally {
        setMutating(false);
      }
    },
    [category, shouldIncludeTag]
  );

  const updateManagedTag = useCallback(
    async (
      id: string,
      updates: Partial<CreateTagRequest>
    ): Promise<Tag | null> => {
      setMutating(true);
      setError(null);
      try {
        await updateTagService(id, updates);
        cacheRef.current.clear();
        const updatedTag = await getTag(id);

        if (!updatedTag) {
          return null;
        }

        setTags((prev) => {
          const filtered = prev.filter((tag) => tag.id !== id);
          if (!shouldIncludeTag(updatedTag)) {
            return filtered;
          }
          filtered.push(updatedTag);
          return sortTags(filtered);
        });

        return updatedTag;
      } catch (err) {
        console.error("Error updating tag:", err);
        const errorToThrow =
          err instanceof Error ? err : new Error("Failed to update tag");
        setError(errorToThrow);
        throw errorToThrow;
      } finally {
        setMutating(false);
      }
    },
    [shouldIncludeTag]
  );

  const deleteManagedTag = useCallback(
    async (id: string): Promise<void> => {
      setMutating(true);
      setError(null);
      try {
        await deleteTagService(id);
        cacheRef.current.clear();
        setTags((prev) => prev.filter((tag) => tag.id !== id));
      } catch (err) {
        console.error("Error deleting tag:", err);
        const errorToThrow =
          err instanceof Error ? err : new Error("Failed to delete tag");
        setError(errorToThrow);
        throw errorToThrow;
      } finally {
        setMutating(false);
      }
    },
    []
  );

  const searchManagedTags = useCallback(
    async (
      searchTerm: string,
      searchOptions: Pick<TagSearchOptions, "category" | "limit" | "includeGlobal"> = {}
    ) => {
      const trimmed = searchTerm.trim();
      if (!trimmed) {
        return [];
      }

      const targetCategory = searchOptions.category ?? category;
      const resultLimit = searchOptions.limit ?? limit ?? 10;
      const includeGlobalResults =
        searchOptions.includeGlobal ?? includeGlobal;

      try {
        if (
          includeGlobalResults &&
          targetCategory &&
          targetCategory !== "global"
        ) {
          const results = await fetchTags({
            category: targetCategory,
            includeGlobal: true,
            searchTerm: trimmed,
            limit: resultLimit,
          });
          return sortTags(results);
        }

        const results = await searchTagsService(
          trimmed,
          targetCategory,
          resultLimit
        );
        return sortTags(results);
      } catch (err) {
        console.error("Error searching tags:", err);
        return [];
      }
    },
    [category, includeGlobal, limit]
  );

  const fetchPopular = useCallback(
    async (targetCategory = category, limitResults = 5): Promise<Tag[]> => {
      setPopularLoading(true);
      setPopularError(null);
      try {
        const popular = await getPopularTagsService(targetCategory, limitResults);
        setPopularTags(popular);
        return popular;
      } catch (err) {
        console.error("Error fetching popular tags:", err);
        const errorToThrow =
          err instanceof Error ? err : new Error("Failed to fetch popular tags");
        setPopularError(errorToThrow);
        return [];
      } finally {
        setPopularLoading(false);
      }
    },
    [category]
  );

  return {
    tags,
    popularTags,
    loading,
    popularLoading,
    error,
    popularError,
    mutating,
    refreshTags,
    searchTags: searchManagedTags,
    createTag: createManagedTag,
    updateTag: updateManagedTag,
    deleteTag: deleteManagedTag,
    fetchPopularTags: fetchPopular,
    clearCache,
  };
};

export type { UseTagManagementOptions, UseTagManagementReturn };
